import { Button, Checkbox, Input, Modal, Popover, Space, Table } from 'antd';
import React, { FC, useMemo, useState } from 'react';
import styles from './index.less';
import { useRequest } from 'umi';
import { DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import cx from 'classnames';
import { TableRowSelection } from 'antd/es/table/interface';
import { searchFreeVideo } from '@/services/ConsoleRequest';
import { cloneDeep } from 'lodash';

type TVideoModalProps = {
  visible: boolean;
  onClose: () => void;
  onOk: (selectVideos: TFrVideoObj[]) => void;
};

export type TFrVideoObj = {
  id: string;
  name: string;
  courseId: string;
  courseName: string;
  duration: number;
  size?: number;
  resolution?: string;
  url: string;
};

const VideoModal: FC<TVideoModalProps> = (props) => {
  const { visible, onClose, onOk } = props;
  const [selectedVideos, setSelectedVideos] = useState<TFrVideoObj[]>([]);
  const [current, setCurrent] = useState(1);
  const [key, setKey] = useState('');
  const [total, setTotal] = useState(0);
  const [videoList, setVideoList] = useState<TFrVideoObj[]>([]);
  const { run, loading } = useRequest(
    (page, text: string) => {
      return searchFreeVideo(page, text);
    },
    {
      refreshDeps: [current],
      manual: true,
      onSuccess: (res, params) => {
        console.log('res', res);
        if (Array.isArray(res.records)) {
          setVideoList(res.records);
          setTotal(res.total);
          setCurrent(params[0]);
        }
      },
    },
  );
  const selectedRowKeys = useMemo(() => {
    return selectedVideos.map((item) => item.id);
  }, [selectedVideos]);
  const onSelectChange = (clickId: string | string[], checked: boolean) => {
    const temp = cloneDeep(selectedVideos);
    const handleClick = (id: string) => {
      const idx = temp.findIndex((tempItem) => tempItem.id === id);
      if (checked) {
        if (idx === -1) {
          const findItem = videoList.find((item) => item.id === id);
          if (findItem) {
            temp.push(findItem);
          }
        }
      } else {
        if (idx > -1) {
          temp.splice(idx, 1);
        }
      }
    };
    if (!Array.isArray(clickId)) {
      handleClick(clickId);
    } else {
      for (const id of clickId) {
        handleClick(id);
      }
    }
    setSelectedVideos(temp);
  };
  const rowSelection: TableRowSelection<TFrVideoObj> = {
    selectedRowKeys,
  };

  const allChecked = useMemo(() => {
    if (selectedVideos.length > 0) {
      for (const video of videoList) {
        if (!selectedVideos.find((item) => item.id === video.id)) {
          return false;
        }
      }
      return true;
    }
    return false;
  }, [selectedVideos, videoList]);

  const haveChecked = useMemo(() => {
    if (allChecked) {
      return false;
    }
    for (const video of videoList) {
      if (selectedVideos.find((item) => item.id === video.id)) {
        return true;
      }
    }
    return false;
  }, [selectedVideos, videoList, allChecked]);

  return (
    <Modal
      wrapClassName={styles.video_modal_wrapper}
      title="选择学院视频"
      okText="添加"
      open={visible}
      onCancel={onClose}
      width={1000}
      centered
      footer={
        <div
          className={cx(
            styles.video_modal_wrapper_footer,
            selectedRowKeys.length > 0 && styles.selected,
          )}
        >
          {selectedRowKeys.length > 0 && (
            <Popover
              overlayClassName={styles.video_select_popover}
              placement="topLeft"
              content={
                <Table<TFrVideoObj>
                  pagination={false}
                  columns={[
                    { dataIndex: 'name', key: 'name', title: '视频名称' },
                    { dataIndex: 'courseName', key: 'courseName', title: '视频课' },
                    {
                      dataIndex: 'operation',
                      key: 'operation',
                      title: '操作',
                      render: (_, record) => {
                        return (
                          <DeleteOutlined
                            className="text-808696 cursor-pointer"
                            onClick={() => {
                              const id = record.id;
                              const idx = selectedRowKeys.indexOf(id);
                              if (idx > -1) {
                                setSelectedVideos((prevState) => {
                                  prevState.splice(idx, 1);
                                  return [...prevState];
                                });
                              }
                            }}
                          />
                        );
                      },
                    },
                  ]}
                  rowKey="id"
                  dataSource={selectedVideos}
                />
              }
            >
              <div className="text-blue cursor-pointer">已选{selectedRowKeys.length}个视频</div>
            </Popover>
          )}
          <Space>
            <Button onClick={onClose}>取消</Button>
            <Button
              type="primary"
              onClick={() => {
                onOk(selectedVideos);
              }}
            >
              添加
            </Button>
          </Space>
        </div>
      }
    >
      <div>
        <div className={cx(styles.tips, styles.row)}>
          <ExclamationCircleOutlined className="text-blue" />
          <div>打卡营暂不支持付费报名，因此仅可选择学院中的免费视频</div>
        </div>
        <Input.Search
          loading={loading}
          onChange={(e) => {
            setKey(e.target.value);
          }}
          onSearch={(value) => {
            return run(1, value);
          }}
          className={cx(styles.input, styles.row)}
          placeholder="可输入视频ID、视频名称、视频课名称搜索视频"
        />
        <Table<TFrVideoObj>
          rowSelection={rowSelection}
          dataSource={videoList}
          rowKey="id"
          pagination={{
            total,
            showSizeChanger: false,
            onChange: (page) => {
              run(page, key);
            },
          }}
          columns={[
            {
              dataIndex: 'selectKey',
              key: 'selectKey',
              title: (
                <Checkbox
                  indeterminate={haveChecked}
                  checked={allChecked}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    const allIds = videoList.map((item) => item.id);
                    onSelectChange(allIds, checked);
                  }}
                  disabled={videoList.length === 0}
                />
              ),
              render: (_, record) => {
                return (
                  <Checkbox
                    checked={selectedRowKeys.includes(record.id)}
                    onChange={(e) => {
                      const value = e.target.checked;
                      onSelectChange(record.id, value);
                    }}
                  />
                );
              },
            },
            { dataIndex: 'id', key: 'id', title: '视频ID' },
            {
              dataIndex: 'name',
              key: 'name',
              title: '视频名称',
              render: (_, record) => {
                return (
                  <a target="_blank" href={`${process.env.EDU_URL}/video/play/${record.id}`}>
                    {record.name}
                  </a>
                );
              },
            },
            {
              dataIndex: 'courseName',
              key: 'courseName',
              title: '视频课',
              render: (_, record) => {
                return (
                  <a target="_blank" href={`${process.env.EDU_URL}/video/${record.courseId}`}>
                    {record.courseName}
                  </a>
                );
              },
            },
          ]}
        />
      </div>
    </Modal>
  );
};
export default VideoModal;
